<template>
    <div
        :class="{
            relative: !isMobile && showExclusionsSidebar,
            'exclusions-sidebar-open': !isMobile && showExclusionsSidebar,
            'exclusions-sidebar-closed': !isMobile && !showExclusionsSidebar,
        }"
    >
        <!-- Page Header (always visible) -->
        <div class="grid grid-cols-12 items-start justify-between relative">
            <div class="col-span-12">
                <div class="flex flex-col items-start pb-8">
                    <div class="flex flex-col items-start max-w-[800px]">
                        <div
                            class="flex flex-col items-start gap-2 self-stretch"
                        >
                            <h1
                                class="text-slate-800 dark:text-slate-100 text-2xl font-semibold leading-8"
                            >
                                My next card
                            </h1>
                            <p
                                class="text-slate-500 dark:text-slate-300 text-sm font-normal leading-5 self-stretch"
                            >
                                The following card rankings are our expert
                                recommendations for you based on a number of
                                criteria.
                            </p>
                        </div>
                        <button
                            type="button"
                            @click="setShowNextCardMethodModal(true)"
                            class="mt-1 -ml-2 p-2 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:text-slate-600 dark:text-slate-300 rounded-lg"
                        >
                            How it works
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="pt-80">
            <LoadingState :loading="loading" />
        </div>

        <!-- Main Content-->
        <div v-else>
            <!-- Search input (UI only) -->
            <div>
                <div
                    class="flex w-full p-2 flex-col items-start gap-2.5 rounded-lg bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50"
                >
                    <div class="relative w-full">
                        <div
                            class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
                        >
                            <Icon
                                icon="search"
                                class="h-5 w-5 text-slate-800 dark:text-slate-100"
                            />
                        </div>
                        <input
                            v-model="search"
                            type="text"
                            class="block w-full pl-12 pr-12 py-2.5 border-0 rounded-lg text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 bg-transparent placeholder-slate-500 focus:outline-none focus:placeholder-slate-400 focus:ring-1 focus:ring-slate-800 focus:ring-inset"
                            placeholder="Search by card, bank"
                            aria-label="Search by card, bank"
                        />
                        <button
                            v-if="search && search.trim() !== ''"
                            type="button"
                            class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100"
                            @click="clearSearch"
                            aria-label="Clear search"
                        >
                            <Icon icon="close" class="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Filters (Desktop) -->
            <div class="hidden md:flex w-full pt-6 items-center gap-4">
                <div class="flex items-center content-center gap-4 flex-wrap">
                    <div
                        v-if="users && users.length > 1"
                        class="flex items-center"
                    >
                        <ButtonPopover
                            buttonClass="btn-base btn-white !pl-3 !pr-2 h-10"
                            popoverClass="inline-block bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 shadow-sm dark:shadow-slate-900/50 z-30 p-0"
                        >
                            <template #button-content>
                                <Icon
                                    icon="user"
                                    class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                />
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium truncate max-w-[200px]"
                                >
                                    {{ activeUserName }}
                                </span>
                                <Icon
                                    icon="chevron-grabber-vertical"
                                    class="w-5 h-5 text-slate-600 dark:text-slate-300"
                                />
                            </template>
                            <template #default="{ closePopover }">
                                <ul
                                    class="inline-block min-w-[168px] w-max p-2"
                                >
                                    <li
                                        v-for="(u, i) in users"
                                        :key="'switch-user-' + i"
                                        class="block w-full"
                                    >
                                        <button
                                            type="button"
                                            @click="
                                                loadNextCards(u);
                                                closePopover();
                                            "
                                            class="w-full text-left p-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-10x-orange flex items-center gap-2 text-slate-800 dark:text-slate-100 whitespace-nowrap cursor-pointer rounded"
                                        >
                                            <Icon
                                                icon="user"
                                                class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                            />
                                            <span>{{ u.name }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </template>
                        </ButtonPopover>
                    </div>
                    <div
                        v-if="users && users.length > 1"
                        class="w-px h-6 bg-slate-200 dark:bg-slate-700"
                    ></div>
                    <!-- Card type -->
                    <ButtonPopover
                        buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                    >
                        <template #button-content>
                            <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                Card type
                            </span>
                            <Transition name="fade">
                                <span
                                    v-if="selectedTypesCount > 0"
                                    class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                                >
                                    {{ selectedTypesCount }}
                                </span>
                            </Transition>
                            <Icon
                                icon="chevron-down-small"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </template>
                        <template #default>
                            <ul>
                                <li
                                    v-for="option in types"
                                    :key="'type-' + option.value"
                                >
                                    <button
                                        type="button"
                                        @click="
                                            toggleFilter(option.value, 'types')
                                        "
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-10x-orange flex items-center gap-2 text-slate-800 dark:text-slate-100 rounded"
                                    >
                                        <input
                                            type="checkbox"
                                            :checked="
                                                filters.types.includes(
                                                    option.value
                                                )
                                            "
                                            class="rounded text-10x-orange"
                                            readonly
                                        />
                                        <span>{{ option.name }}</span>
                                    </button>
                                </li>
                                <li v-if="selectedTypesCount > 0">
                                    <button
                                        type="button"
                                        @click="clearFilters()"
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none text-slate-800 dark:text-slate-100"
                                    >
                                        Reset
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </ButtonPopover>

                    <!-- Rewards (Programs) -->
                    <ButtonPopover
                        buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                    >
                        <template #button-content>
                            <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                Rewards
                            </span>
                            <Transition name="fade">
                                <span
                                    v-if="selectedProgramsCount > 0"
                                    class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                                >
                                    {{ selectedProgramsCount }}
                                </span>
                            </Transition>
                            <Icon
                                icon="chevron-down-small"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </template>
                        <template #default>
                            <ul>
                                <li
                                    v-for="option in programTypes"
                                    :key="'program-' + option.type"
                                >
                                    <button
                                        type="button"
                                        @click="
                                            toggleFilter(
                                                option.type,
                                                'programs'
                                            )
                                        "
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-10x-orange flex items-center gap-2 text-slate-800 dark:text-slate-100 rounded"
                                    >
                                        <input
                                            type="checkbox"
                                            :checked="
                                                filters.programs.includes(
                                                    option.type
                                                )
                                            "
                                            class="rounded text-10x-orange"
                                            readonly
                                        />
                                        <span>
                                            {{ goodProgramName(option.type) }}
                                            Rewards
                                        </span>
                                    </button>
                                </li>
                                <li v-if="selectedProgramsCount > 0">
                                    <button
                                        type="button"
                                        @click="clearFilters()"
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none text-slate-800 dark:text-slate-100"
                                    >
                                        Reset
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </ButtonPopover>

                    <!-- Bank -->
                    <ButtonPopover
                        buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                        popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-slate-900/50 min-w-80 z-30 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-600 p-2"
                    >
                        <template #button-content>
                            <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                Bank
                            </span>
                            <Transition name="fade">
                                <span
                                    v-if="selectedBanksCount > 0"
                                    class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                                >
                                    {{ selectedBanksCount }}
                                </span>
                            </Transition>
                            <Icon
                                icon="chevron-down-small"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </template>
                        <template #default>
                            <div
                                class="px-4 py-2.5 bg-white dark:bg-slate-700 rounded-lg justify-start items-center gap-2 flex w-full text-slate-800 dark:text-slate-100"
                            >
                                <Icon icon="search" />
                                <div class="w-full">
                                    <label for="bank-search" class="sr-only">
                                        Search banks
                                    </label>
                                    <input
                                        id="bank-search"
                                        type="text"
                                        placeholder="Search banks..."
                                        v-model="bankSearchQuery"
                                        class="p-0 border-none w-full h-full focus:ring-0 relative text-sm font-normal leading-tight placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm placeholder:font-normal"
                                        autocomplete="off"
                                    />
                                </div>
                            </div>
                            <div class="w-full h-px bg-slate-200 dark:bg-slate-700"></div>
                            <ul class="max-h-80 overflow-y-auto">
                                <li
                                    v-for="option in filteredBanks"
                                    :key="'bank-' + option.code"
                                >
                                    <button
                                        type="button"
                                        @click="
                                            toggleFilter(option.code, 'banks')
                                        "
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-10x-orange flex items-center gap-3 text-slate-800 dark:text-slate-100 rounded"
                                    >
                                        <input
                                            type="checkbox"
                                            :checked="
                                                filters.banks.includes(
                                                    option.code
                                                )
                                            "
                                            class="rounded text-10x-orange"
                                            readonly
                                        />
                                        <img
                                            v-if="shouldShowLogo(option)"
                                            :src="bankLogo(option)"
                                            alt=""
                                            class="w-5 h-5 rounded-full"
                                            @error="
                                                onBankLogoError($event, option)
                                            "
                                        />
                                        <div
                                            v-else
                                            class="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] text-slate-600 dark:text-slate-300 uppercase"
                                        >
                                            {{ getBankInitials(option) }}
                                        </div>
                                        <span>{{ option.name }}</span>
                                    </button>
                                </li>
                            </ul>
                            <p
                                v-if="filteredBanks.length === 0"
                                class="p-3 bg-white dark:bg-slate-700 rounded-lg text-slate-800 dark:text-slate-100 text-sm leading-tight"
                            >
                                No results found, try adjusting your search
                            </p>
                        </template>
                    </ButtonPopover>

                    <!-- Spend required / Annual fee popover shows fees count -->
                    <ButtonPopover
                        buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                    >
                        <template #button-content>
                            <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                Annual fee
                            </span>
                            <Transition name="fade">
                                <span
                                    v-if="selectedFeesCount > 0"
                                    class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                                >
                                    {{ selectedFeesCount }}
                                </span>
                            </Transition>
                            <Icon
                                icon="chevron-down-small"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </template>
                        <template #default>
                            <ul>
                                <li
                                    v-for="option in annualFeeOptions"
                                    :key="'fee-' + option.value"
                                >
                                    <button
                                        type="button"
                                        @click="
                                            toggleFilter(option.value, 'fees')
                                        "
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-10x-orange flex items-center gap-2 text-slate-800 dark:text-slate-100 rounded"
                                    >
                                        <input
                                            type="checkbox"
                                            :checked="
                                                filters.fees?.includes(
                                                    option.value
                                                )
                                            "
                                            class="rounded text-10x-orange"
                                            readonly
                                        />
                                        <span>{{ option.label }}</span>
                                    </button>
                                </li>
                                <li v-if="selectedFeesCount > 0">
                                    <button
                                        type="button"
                                        @click="clearFilters()"
                                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none text-slate-800 dark:text-slate-100"
                                    >
                                        Reset
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </ButtonPopover>

                    <!-- All filters opens existing sidebar (mobile overlay logic reused) -->
                    <button
                        type="button"
                        @click="toggleFiltersMenu()"
                        class="btn-base btn-white !pl-4 !pr-3 h-10 inline-flex items-center gap-2"
                    >
                        <Icon icon="filters" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            All filters
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="totalSelections > 0"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ totalSelections }}
                            </span>
                        </Transition>
                    </button>
                    <button
                        v-if="hasAnySelections"
                        type="button"
                        @click="resetAll"
                        class="flex items-center gap-2 text-slate-700 dark:text-slate-200 text-sm font-medium hover:text-slate-900 dark:text-slate-100 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1 active:ring-2 active:ring-slate-400"
                    >
                        <span>Reset all</span>
                        <Icon icon="close" class="w-4 h-4 text-slate-700 dark:text-slate-200" />
                    </button>
                </div>
            </div>

            <!-- Count + Display row (Desktop) -->
            <div
                v-if="!isMobile && !loading"
                class="hidden md:flex w-full pt-8 pb-10 justify-between items-center min-h-[52px]"
            >
                <p
                    v-if="isSearching && search && search.trim() !== ''"
                    class="text-slate-500 dark:text-slate-300 text-sm font-normal leading-5"
                >
                    Searching cards...
                </p>
                <p v-else class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                    {{ searchedCount }} card recommendations
                </p>
                <ButtonPopover
                    buttonClass="flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors group"
                >
                    <template #button-content>
                        <Icon
                            icon="settings"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100 group-hover:text-slate-700 dark:text-slate-200 group-active:text-slate-700 dark:text-slate-200"
                        />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium group-hover:text-slate-700 dark:text-slate-200 group-active:text-slate-700 dark:text-slate-200"
                        >
                            Display
                        </span>
                        <Icon
                            icon="chevron-down"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100 group-hover:text-slate-700 dark:text-slate-200 group-active:text-slate-700 dark:text-slate-200"
                        />
                    </template>
                    <template #default>
                        <div class="w-64">
                            <div
                                class="flex items-center justify-between px-2 py-2"
                            >
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    Show 5/24 count
                                </span>
                                <label
                                    class="switch relative inline-block w-[42px] h-[25px] shrink-0"
                                >
                                    <input
                                        class="opacity-0 w-0 h-0"
                                        type="checkbox"
                                        :checked="show524"
                                        @change="
                                            setShow524($event.target.checked)
                                        "
                                    />
                                    <span
                                        class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-600 transition duration-400 rounded-xl"
                                    ></span>
                                </label>
                            </div>
                            <div
                                class="flex items-center justify-between px-2 py-2"
                            >
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    Show unavailable cards
                                </span>
                                <label
                                    class="switch relative inline-block w-[42px] h-[25px] shrink-0"
                                >
                                    <input
                                        class="opacity-0 w-0 h-0"
                                        type="checkbox"
                                        :checked="showHidden"
                                        @change="
                                            setShowHidden($event.target.checked)
                                        "
                                    />
                                    <span
                                        class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-600 transition duration-400 rounded-xl"
                                    ></span>
                                </label>
                            </div>
                        </div>
                    </template>
                </ButtonPopover>
            </div>

            <!-- Mobile Filters Section -->
            <div
                v-if="isMobile && !loading"
                class="md:hidden pt-4 flex items-center gap-2 self-stretch"
            >
                <div
                    style="
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        flex: 1 0 0;
                    "
                >
                    <!-- User Filter -->
                    <div
                        v-if="users && users.length > 1"
                        class="flex items-center"
                    >
                        <button
                            type="button"
                            @click="openSwitchUserOverlay"
                            class="btn-base btn-white !pl-4 !pr-3 h-10 !mt-0 inline-flex items-center gap-2"
                        >
                            <Icon icon="user" class="w-4 h-4 text-slate-800 dark:text-slate-100" />
                            <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                {{ activeUser.name }}
                            </span>
                            <Icon
                                icon="chevron-down-small"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                    </div>

                    <!-- All Filters -->
                    <button
                        type="button"
                        @click="openMobileFiltersModal()"
                        class="btn-base btn-white !pl-4 !pr-3 h-10 !mt-0 inline-flex items-center gap-2"
                    >
                        <Icon icon="filters" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            All filters
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="totalSelections > 0"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ totalSelections }}
                            </span>
                        </Transition>
                    </button>
                </div>

                <!-- Display Icon -->
                <button
                    type="button"
                    @click="openDisplayOverlay()"
                    class="btn-icon btn-white !mt-0 h-10"
                >
                    <Icon icon="settings" class="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </button>
            </div>

            <!-- Mobile Count (below filters) -->
            <div
                v-if="isMobile && !loading"
                class="md:hidden flex items-center gap-3 self-stretch pt-6 pb-6"
            >
                <p
                    v-if="isSearching && search && search.trim() !== ''"
                    class="text-slate-500 dark:text-slate-300 text-base"
                >
                    Searching cards...
                </p>
                <p v-else class="text-slate-500 dark:text-slate-300 text-base">
                    {{ searchedCount }} card recommendations
                </p>
            </div>

            <div class="my-next-card-wrapper grid grid-cols-12 gap-4">
                <div
                    class="hidden xl:block col-span-12 relative h-full w-full max-w-xs flex-col overflow-y-auto"
                >
                    <h2 class="sr-only">Filters</h2>

                </div>

                <div class="hidden">
                    <next-card-filters
                        :institutions="institutions"
                    ></next-card-filters>
                </div>

                <div
                    class="recommended-cards col-span-12 space-y-6 md:space-y-8"
                >
                    <div class="grid grid-cols-12 gap-4 relative w-full">
                        <div
                            class="col-span-12 flex flex-col items-center justify-center text-center min-h-[60vh]"
                            v-if="
                                visibleFilteredNextCards.length === 0 &&
                                !topCardMatchesSearch &&
                                loaded &&
                                debouncedSearch &&
                                debouncedSearch.trim() !== '' &&
                                !isSearching
                            "
                        >
                            <div
                                class="flex w-16 h-16 justify-center items-center gap-2.5 rounded-full bg-slate-200 dark:bg-slate-700"
                            >
                                <Icon
                                    icon="search"
                                    class="w-8 h-8 shrink-0 text-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <div class="flex flex-col items-center gap-2 pt-6">
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
                                >
                                    No matching cards found
                                </h3>
                                <p
                    class="text-slate-500 dark:text-slate-300 text-sm font-normal leading-5"
                                >
                                    Try changing your search parameters.
                                </p>
                            </div>
                        </div>

                        <!-- Over 5/24 + Chase Filter State -->
                        <div
                            class="col-span-12 flex flex-col items-center justify-center text-center min-h-[60vh]"
                            v-else-if="showOver524ChaseState"
                        >
                            <div
                                class="flex w-16 h-16 justify-center items-center gap-2.5 rounded-full bg-slate-200 dark:bg-slate-700"
                            >
                                <Icon
                                    icon="credit-cards"
                                    class="w-8 h-8 shrink-0 text-slate-600 dark:text-slate-300"
                                />
                            </div>
                            <div class="flex flex-col items-center gap-2 pt-6">
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
                                >
                                    You're Over 5/24
                                </h3>
                                <p
                                    class="text-slate-500 dark:text-slate-300 text-sm font-normal leading-5 max-w-md"
                                >
                                    You're currently
                                    {{ fiveTwentyFourCount }}/24. To be approved
                                    for a new credit card from Chase, you can't
                                    have opened five or more personal credit
                                    cards across all banks within the past 24
                                    months. This means that you must be under
                                    five personal credit cards to be approved.
                                </p>
                                <a
                                    href="https://10xtravel.com/chase-5-24-rule/"
                                    target="_blank"
                                    class="text-slate-600 dark:text-slate-300 text-sm font-medium hover:text-slate-800 dark:text-slate-100 transition-colors duration-200 mt-2"
                                >
                                    Learn more
                                </a>
                            </div>
                            <div class="flex flex-col items-start gap-2 pt-6">
                                <button
                                    type="button"
                                    @click="clearFilters"
                                    class="inline-flex items-start gap-2 px-4 py-2.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>

                        <div
                            class="col-span-12 flex flex-col items-center justify-center text-center min-h-[60vh]"
                            v-else-if="
                                visibleFilteredNextCards.length === 0 &&
                                !topCardMatchesSearch &&
                                loaded &&
                                (!debouncedSearch ||
                                    debouncedSearch.trim() === '') &&
                                hasAnySelections &&
                                !isSearching
                            "
                        >
                            <div
                                class="flex w-16 h-16 justify-center items-center gap-2.5 rounded-full bg-slate-200 dark:bg-slate-700"
                            >
                                <Icon
                                    icon="filters"
                                    class="w-8 h-8 shrink-0 text-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <div class="flex flex-col items-center gap-2 pt-6">
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
                                >
                                    No matching cards
                                </h3>
                                <p
                                    class="text-slate-500 dark:text-slate-300 text-sm font-normal leading-5"
                                >
                                    Try removing some filters to see more cards.
                                </p>
                            </div>
                            <div class="flex flex-col items-start gap-2 pt-6">
                                <button
                                    type="button"
                                    @click="clearFilters"
                                    class="inline-flex items-start gap-2 px-4 py-2.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>

                        <div
                            class="col-span-12 flex flex-col items-center justify-center text-center min-h-[60vh]"
                            v-else-if="
                                visibleFilteredNextCards.length === 0 &&
                                !topCardMatchesSearch &&
                                loaded &&
                                (!debouncedSearch ||
                                    debouncedSearch.trim() === '') &&
                                !hasAnySelections &&
                                !isSearching
                            "
                        >
                            <div
                                class="flex w-16 h-16 justify-center items-center gap-2.5 rounded-full bg-slate-200 dark:bg-slate-700"
                            >
                                <Icon
                                    icon="credit-card"
                                    class="w-8 h-8 shrink-0 text-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <div class="flex flex-col items-center gap-2 pt-6">
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
                                >
                                    No recommendations yet
                                </h3>
                                <p
                                    class="text-slate-500 dark:text-slate-300 text-sm font-normal leading-5"
                                >
                                    It looks like we don't have any
                                    recommendations for you right now.
                                </p>
                            </div>
                        </div>

                        <TopCard
                            :card="topCard"
                            :user="activeUser"
                            v-if="pageNumber === 0 && topCardMatchesSearch"
                        />

                        <template v-for="(nextCard, n) in visiblePaginatedNextCards" :key="'nc-wrapper-' + n">
                            <template v-if="useLazyMount && n >= 1">
                                <LazyMount :rootMargin="lazyRootMargin" wrapperClass="col-span-12 block min-h-px">
                                    <NextCardCard
                                        :key="'next-card-' + n"
                                        :nextCard="nextCard"
                                        :rank="pageNumber * pageSize + n + 2"
                                        :offenders="offenders"
                                    />
                                </LazyMount>
                            </template>
                            <template v-else>
                                <div class="col-span-12">
                                    <NextCardCard
                                        :key="'next-card-' + n"
                                        :nextCard="nextCard"
                                        :rank="pageNumber * pageSize + n + 2"
                                        :offenders="offenders"
                                    />
                                </div>
                            </template>
                        </template>
                    </div>

                    <div class="mt-8 custom-pagination" v-if="uiPageCount > 1">
                        <Pagination
                            :totalResults="searchedCount"
                            :totalPages="uiPageCount"
                            :perPage="pageSize"
                            :currentPage="pageNumber + 1"
                            :maxVisibleButtons="5"
                            noun="cards"
                            separator="-"
                            @pageChanged="handlePageChange"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile Switch User Overlay -->
        <transition name="fade">
            <div
                v-if="showSwitchUserOverlay && isMobile"
                class="fixed inset-0 z-50"
            >
                <div
                    class="absolute inset-0 bg-black/40"
                    @click="closeSwitchUserOverlay"
                ></div>
                <div
                    class="absolute left-0 right-0 bottom-0 rounded-2xl bg-white dark:bg-slate-800 shadow-xl dark:shadow-slate-900/50 p-4"
                >
                    <div class="flex items-center justify-between mb-2">
                        <h3 class="text-slate-800 dark:text-slate-100 text-lg font-semibold">
                            Show best cards for
                        </h3>
                        <button
                            class="p-2 -mr-2"
                            @click="closeSwitchUserOverlay"
                            aria-label="Close"
                        >
                            <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                        </button>
                    </div>
                    <div>
                        <label
                            v-for="u in users || []"
                            :key="
                                'desktop-user-' + (u?.id ?? u?.uuid ?? u?.name)
                            "
                            class="flex items-center gap-3 py-3 cursor-pointer"
                        >
                            <input
                                type="radio"
                                class="text-10x-orange"
                                :value="u?.id ?? u?.uuid ?? u?.name"
                                v-model="tempSelectedUserId"
                            />
                            <span class="text-slate-800 dark:text-slate-100 text-sm">
                                {{ u?.name }}
                            </span>
                        </label>
                    </div>
                    <button
                        type="button"
                        class="mt-4 w-full h-11 rounded-lg bg-slate-800 text-white text-sm font-medium"
                        @click="applySwitchUserOverlay"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </transition>

        <!-- Mobile Display Settings Overlay -->
        <transition name="fade">
            <div
                v-if="showDisplayOverlay && isMobile"
                class="fixed inset-0 z-50"
            >
                <div
                    class="absolute inset-0 bg-black/40"
                    @click="closeDisplayOverlay"
                ></div>
                <div
                    class="absolute left-0 right-0 bottom-0 shadow-xl"
                    style="
                        display: flex;
                        padding-bottom: 48px;
                        flex-direction: column;
                        align-items: flex-start;
                        position: absolute;
                        bottom: 0;
                        border-radius: var(--Tailwind-16, 16px)
                            var(--Tailwind-16, 16px) var(--Tailwind-0, 0)
                            var(--Tailwind-0, 0);
                        background: var(--Background-White, #fff);
                    "
                >
                    <div
                        style="
                            display: flex;
                            padding: 24px 72px 16px 24px;
                            flex-direction: column;
                            align-items: flex-start;
                            gap: 8px;
                            align-self: stretch;
                            position: relative;
                        "
                    >
                        <div
                            style="
                                color: var(--text-icons-primary, #1e293b);
                                font-size: 20px;
                                font-style: normal;
                                font-weight: 500;
                                line-height: 28px;
                            "
                        >
                            Display settings
                        </div>
                        <button
                            class="p-2"
                            @click="closeDisplayOverlay"
                            aria-label="Close"
                            style="position: absolute; top: 16px; right: 16px"
                        >
                            <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                        </button>
                    </div>
                    <div
                        style="
                            display: flex;
                            padding: 4px 4px 0 24px;
                            flex-direction: column;
                            align-items: flex-start;
                            align-self: stretch;
                            width: 100%;
                        "
                    >
                        <div
                            style="
                                display: flex;
                                padding: 12px 0;
                                align-items: center;
                                gap: 12px;
                                align-self: stretch;
                                border-radius: var(--radius-md-base, 4px);
                            "
                        >
                            <span
                                style="
                                    flex: 1 0 0;
                                    color: var(--text-icons-primary, #1e293b);
                                    font-size: 16px;
                                    font-style: normal;
                                    font-weight: 400;
                                    line-height: 24px;
                                "
                            >
                                Show 5/24 count
                            </span>
                            <label
                                class="switch relative inline-block w-[42px] h-[25px] shrink-0"
                            >
                                <input
                                    class="opacity-0 w-0 h-0"
                                    type="checkbox"
                                    :checked="show524"
                                    @change="setShow524($event.target.checked)"
                                />
                                <span
                                    class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-600 transition duration-400 rounded-xl"
                                ></span>
                            </label>
                        </div>
                        <div
                            style="
                                display: flex;
                                padding: 12px 0;
                                align-items: center;
                                gap: 12px;
                                align-self: stretch;
                                border-radius: var(--radius-md-base, 4px);
                            "
                        >
                            <span
                                style="
                                    flex: 1 0 0;
                                    color: var(--text-icons-primary, #1e293b);
                                    font-size: 16px;
                                    font-style: normal;
                                    font-weight: 400;
                                    line-height: 24px;
                                "
                            >
                                Show unavailable cards
                            </span>
                            <label
                                class="switch relative inline-block w-[42px] h-[25px] shrink-0"
                            >
                                <input
                                    class="opacity-0 w-0 h-0"
                                    type="checkbox"
                                    :checked="showHidden"
                                    @change="
                                        setShowHidden($event.target.checked)
                                    "
                                />
                                <span
                                    class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-600 transition duration-400 rounded-xl"
                                ></span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <ExclusionsSidebar />

        <!-- Mobile Filters Modal -->
        <UiModalMobile
            :isOpen="isMobileFiltersOpen"
            title="Filters"
            @close="isMobileFiltersOpen = false"
        >
            <template #content>
                <div class="space-y-6">
                    <!-- Card type -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Card type
                        </div>
                        <div class="mt-4 space-y-3">
                            <label
                                v-for="option in types"
                                :key="'m-type-' + option.value"
                                class="flex items-center gap-3 cursor-pointer select-none"
                                @click.prevent="
                                    toggleFilter(option.value, 'types')
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="
                                        filters.types.includes(option.value)
                                    "
                                    @click.stop
                                    @change="
                                        toggleFilter(option.value, 'types')
                                    "
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    {{ option.name }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <hr class="border-slate-200 dark:border-slate-700" />

                    <!-- Rewards (Programs) -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Rewards
                        </div>
                        <div class="mt-4 space-y-3">
                            <label
                                v-for="option in programTypes"
                                :key="'m-prog-' + option.type"
                                class="flex items-center gap-3 cursor-pointer select-none"
                                @click.prevent="
                                    toggleFilter(option.type, 'programs')
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="
                                        filters.programs.includes(option.type)
                                    "
                                    @click.stop
                                    @change="
                                        toggleFilter(option.type, 'programs')
                                    "
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    {{ goodProgramName(option.type) }} Rewards
                                </span>
                            </label>
                        </div>
                    </div>

                    <hr class="border-slate-200 dark:border-slate-700" />

                    <!-- Bank -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium mb-3">
                            Bank
                        </div>
                        <div class="mb-3">
                            <div class="relative">
                                <Icon
                                    icon="search"
                                    class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-300"
                                />
                                <input
                                    type="text"
                                    placeholder="Search banks..."
                                    v-model="bankSearchQuery"
                                    class="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                />
                            </div>
                        </div>
                        <div class="space-y-3 max-h-60 overflow-y-auto">
                            <label
                                v-for="option in filteredBanks"
                                :key="'m-bank-' + option.code"
                                class="flex items-center gap-3 cursor-pointer select-none"
                                @click.prevent="
                                    toggleFilter(option.code, 'banks')
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="
                                        filters.banks.includes(option.code)
                                    "
                                    @click.stop
                                    @change="toggleFilter(option.code, 'banks')"
                                />
                                <img
                                    v-if="shouldShowLogo(option)"
                                    :src="bankLogo(option)"
                                    alt=""
                                    class="w-5 h-5 rounded-full"
                                    @error="onBankLogoError($event, option)"
                                />
                                <div
                                    v-else
                                    class="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] text-slate-600 dark:text-slate-300 uppercase"
                                >
                                    {{ getBankInitials(option) }}
                                </div>
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    {{ option.name }}
                                </span>
                            </label>
                            <p
                                v-if="filteredBanks.length === 0"
                                class="text-slate-500 dark:text-slate-300 text-sm text-center py-4"
                            >
                                No results found, try adjusting your search
                            </p>
                        </div>
                    </div>

                    <hr class="border-slate-200 dark:border-slate-700" />

                    <!-- Annual fee -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Annual fee
                        </div>
                        <div class="mt-4 space-y-3">
                            <label
                                v-for="option in annualFeeOptions"
                                :key="'m-fee-' + option.value"
                                class="flex items-center gap-3 cursor-pointer select-none"
                                @click.prevent="
                                    toggleFilter(option.value, 'fees')
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="
                                        filters.fees?.includes(option.value)
                                    "
                                    @click.stop
                                    @change="toggleFilter(option.value, 'fees')"
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    {{ option.label }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <hr class="border-slate-200 dark:border-slate-700" />

                    <!-- Spend required -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Spend required
                        </div>
                        <div class="mt-4 space-y-3">
                            <label
                                v-for="spend in spends"
                                :key="'m-spend-' + spend.value"
                                class="flex items-center gap-3 cursor-pointer select-none"
                                @click.prevent="
                                    toggleFilter(spend.value, 'spends')
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="
                                        filters.spends.includes(spend.value)
                                    "
                                    @click.stop
                                    @change="
                                        toggleFilter(spend.value, 'spends')
                                    "
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    ${{ $filters.formatThousand(spend.value) }}
                                    <span v-if="spend.value !== 0">
                                        and below
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <hr class="border-slate-200 dark:border-slate-700" />

                    <!-- Welcome bonus -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Welcome bonus
                        </div>
                        <div class="mt-4 space-y-3">
                            <label
                                v-for="label in [
                                    'None',
                                    '10,000-24,999 points',
                                    '25,000-44,999 points',
                                    '45,000-64,999 points',
                                    '65,000-79,999 points',
                                    '80,000+ points',
                                ]"
                                :key="'m-wb-' + label"
                                class="flex items-center gap-3 cursor-pointer select-none"
                                @click.prevent="
                                    toggleFilter(label, 'welcomeBonus')
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="
                                        filters.welcomeBonus.includes(label)
                                    "
                                    @click.stop
                                    @change="
                                        toggleFilter(label, 'welcomeBonus')
                                    "
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    {{ label }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <hr class="border-slate-200 dark:border-slate-700" />

                    <!-- Bonus category -->
                    <div>
                        <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Bonus category
                        </div>
                        <div class="mt-4 space-y-3 max-h-60 overflow-y-auto">
                            <template
                                v-if="availableBonusCategories.length > 0"
                            >
                                <label
                                    v-for="category in availableBonusCategories"
                                    :key="'m-cat-' + category.value"
                                    class="flex items-center gap-3 cursor-pointer select-none"
                                    @click.prevent="
                                        toggleFilter(
                                            category.value,
                                            'bonusCategories'
                                        )
                                    "
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="
                                            filters.bonusCategories.includes(
                                                category.value
                                            )
                                        "
                                        @click.stop
                                        @change="
                                            toggleFilter(
                                                category.value,
                                                'bonusCategories'
                                            )
                                        "
                                    />
                                    <span class="text-slate-800 dark:text-slate-100 text-sm">
                                        {{ category.label }}
                                    </span>
                                </label>
                            </template>
                            <div
                                v-else
                                class="text-sm text-slate-500 dark:text-slate-300 text-center py-4"
                            >
                                <p>Loading category data...</p>
                                <p class="text-xs mt-2">
                                    If categories don't appear, the data may not
                                    be available yet.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex items-center justify-between">
                    <button
                        type="button"
                        class="px-4 py-2.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-600"
                        @click="handleClearAllMobile"
                    >
                        Clear all
                    </button>
                    <button
                        type="button"
                        class="px-4 py-2.5 rounded bg-slate-800 dark:bg-slate-700 text-white text-sm font-medium hover:bg-slate-700 dark:hover:bg-slate-600"
                        @click="applyMobileFilters"
                    >
                        Show cards
                    </button>
                </div>
            </template>
        </UiModalMobile>

        <!-- Mobile Display Sidebar -->
        <BaseSidebar
            :isOpen="isMobileDisplayOpen"
            title="Display"
            @close="isMobileDisplayOpen = false"
            :customClasses="[
                'left-0 right-0 top-0 bottom-0 max-w-none w-full rounded-none border-0 ring-0',
                'md:right-3 md:left-auto md:bottom-3 md:top-0 md:max-w-sm md:w-auto md:rounded-lg md:border md:border-slate-200 dark:border-slate-700 md:ring-1 md:ring-white/5',
            ]"
        >
            <template #default>
                <div class="px-6 pt-2 pb-6 space-y-4">
                    <div class="flex items-center justify-between">
                        <span class="text-slate-800 dark:text-slate-100 text-sm">
                            Show 5/24 count
                        </span>
                        <label
                            class="switch relative inline-block w-[42px] h-[25px]"
                        >
                            <input
                                class="opacity-0 w-0 h-0"
                                type="checkbox"
                                :checked="show524"
                                @change="setShow524($event.target.checked)"
                            />
                            <span
                                class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-600 transition duration-400 rounded-xl"
                            ></span>
                        </label>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-slate-800 dark:text-slate-100 text-sm">
                            Show unavailable cards
                        </span>
                        <label
                            class="switch relative inline-block w-[42px] h-[25px]"
                        >
                            <input
                                class="opacity-0 w-0 h-0"
                                type="checkbox"
                                :checked="showHidden"
                                @change="setShowHidden($event.target.checked)"
                            />
                            <span
                                class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-600 transition duration-400 rounded-xl"
                            ></span>
                        </label>
                    </div>
                </div>
            </template>
        </BaseSidebar>

        <UiModal
            v-if="!isMobile"
            :isOpen="showFiltersMenu"
            @close="toggleFiltersMenu()"
            title="All filters"
        >
            <template #content>
                <div class="space-y-6">
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Card type
                        </h3>
                        <div class="py-2">
                            <label
                                v-for="option in types"
                                :key="'m-type-' + option.value"
                                class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    :checked="
                                        filters.types.includes(option.value)
                                    "
                                    @change="
                                        toggleFilter(option.value, 'types')
                                    "
                                />
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    {{ option.name }}
                                </span>
                            </label>
                        </div>
                    </section>

                    <hr class="border-slate-200 dark:border-slate-700" />
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Rewards
                        </h3>
                        <div class="py-2">
                            <label
                                v-for="option in programTypes"
                                :key="'m-prog-' + option.type"
                                class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    :checked="
                                        filters.programs.includes(option.type)
                                    "
                                    @change="
                                        toggleFilter(option.type, 'programs')
                                    "
                                />
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    {{ goodProgramName(option.type) }} Rewards
                                </span>
                            </label>
                        </div>
                    </section>

                    <hr class="border-slate-200 dark:border-slate-700" />
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Bank
                        </h3>
                        <div class="mb-3">
                            <div class="relative">
                                <Icon
                                    icon="search"
                                    class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-300"
                                />
                                <input
                                    type="text"
                                    placeholder="Search banks..."
                                    v-model="bankSearchQuery"
                                    class="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-slate-800 focus:border-slate-800"
                                />
                            </div>
                        </div>
                        <div class="py-2 max-h-80 overflow-y-auto">
                            <label
                                v-for="option in filteredBanks"
                                :key="'m-bank-' + option.code"
                                class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-3 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    :checked="
                                        filters.banks.includes(option.code)
                                    "
                                    @change="toggleFilter(option.code, 'banks')"
                                />
                                <img
                                    v-if="shouldShowLogo(option)"
                                    :src="bankLogo(option)"
                                    alt=""
                                    class="w-5 h-5 rounded-full"
                                    @error="onBankLogoError($event, option)"
                                />
                                <div
                                    v-else
                                    class="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] text-slate-600 dark:text-slate-300 uppercase"
                                >
                                    {{ getBankInitials(option) }}
                                </div>
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    {{ option.name }}
                                </span>
                            </label>
                            <p
                                v-if="filteredBanks.length === 0"
                                class="text-slate-500 dark:text-slate-300 text-sm text-center py-4"
                            >
                                No results found, try adjusting your search
                            </p>
                        </div>
                    </section>

                    <hr class="border-slate-200 dark:border-slate-700" />
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Annual fee
                        </h3>
                        <div class="py-2">
                            <label
                                v-for="option in annualFeeOptions"
                                :key="'m-fee-' + option.value"
                                class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    :checked="
                                        filters.fees?.includes(option.value)
                                    "
                                    @change="toggleFilter(option.value, 'fees')"
                                />
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    {{ option.label }}
                                </span>
                            </label>
                        </div>
                    </section>

                    <hr class="border-slate-200 dark:border-slate-700" />
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Spend required
                        </h3>
                        <div class="py-2">
                            <label
                                v-for="spend in spends"
                                :key="'m-spendreq-' + spend.value"
                                class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    :checked="
                                        filters.spends.includes(spend.value)
                                    "
                                    @change="
                                        toggleFilter(spend.value, 'spends')
                                    "
                                />
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    ${{ $filters.formatThousand(spend.value) }}
                                    <span v-if="spend.value !== 0">
                                        and below
                                    </span>
                                </span>
                            </label>
                        </div>
                    </section>

                    <hr class="border-slate-200 dark:border-slate-700" />
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Welcome bonus
                        </h3>
                        <div class="py-2">
                            <label
                                v-for="label in [
                                    'None',
                                    '10,000-24,999 points',
                                    '25,000-44,999 points',
                                    '45,000-64,999 points',
                                    '65,000-79,999 points',
                                    '80,000+ points',
                                ]"
                                :key="'m-wb-' + label"
                                class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    :checked="
                                        filters.welcomeBonus.includes(label)
                                    "
                                    @change="
                                        toggleFilter(label, 'welcomeBonus')
                                    "
                                />
                                <span class="text-sm text-slate-800 dark:text-slate-100">
                                    {{ label }}
                                </span>
                            </label>
                        </div>
                    </section>

                    <hr class="border-slate-200 dark:border-slate-700" />
                    <section class="w-full">
                        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
                            Bonus category
                        </h3>
                        <div class="py-2 max-h-80 overflow-y-auto">
                            <template
                                v-if="availableBonusCategories.length > 0"
                            >
                                <label
                                    v-for="category in availableBonusCategories"
                                    :key="'m-cat-' + category.value"
                                    class="w-full p-2 text-sm rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        class="form-checkbox"
                                        :checked="
                                            filters.bonusCategories.includes(
                                                category.value
                                            )
                                        "
                                        @change="
                                            toggleFilter(
                                                category.value,
                                                'bonusCategories'
                                            )
                                        "
                                    />
                                    <span class="text-sm text-slate-800 dark:text-slate-100">
                                        {{ category.label }}
                                    </span>
                                </label>
                            </template>
                            <div
                                v-else
                                class="text-sm text-slate-500 dark:text-slate-300 text-center py-4"
                            >
                                <p>Loading category data...</p>
                                <p class="text-xs mt-2">
                                    If categories don't appear, the data may not
                                    be available yet.
                                </p>
                            </div>
                        </div>
                    </section>
                </div>
            </template>
            <template #footer>
                <div
                    class="flex justify-between py-4 border-t border-slate-200 dark:border-slate-700"
                >
                    <button
                        class="btn-base btn-white"
                        @click="handleClearAllMobile"
                        :disabled="totalSelections === 0"
                    >
                        Clear all
                    </button>
                    <button
                        class="btn-base btn-dark"
                        @click="toggleFiltersMenu()"
                    >
                        Show cards
                    </button>
                </div>
            </template>
        </UiModal>

        <button
            v-show="showScrollTop"
            type="button"
            @click="scrollToTop"
            class="fixed right-5 z-50 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-lg dark:shadow-slate-900/50 p-3 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            :style="scrollTopPosStyle"
            id="hs-next-card-scrolltop"
            aria-label="Back to top"
        >
            <span class="sr-only">Back to top</span>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="w-5 h-5"
                aria-hidden="true"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 15l7-7 7 7"
                />
            </svg>
        </button>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import {
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    onBeforeUnmount,
} from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useAppStore } from '@/store/app';
import { useNextCardStore } from '@/store/nextcard';
import { useDashboardStore } from '@/store/dashboard';
import { useWalletStore } from '@/store/wallet';
import { generateUUID } from '@/utils/uuid.js';

import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import NextCardCard from '@/components/next-card/next-card-card.vue';
import NextCardFilters from '@/components/next-card/next-card-filters.vue';
import TopCard from '@/components/next-card/top-card.vue';
import ExclusionsSidebar from '@/components/next-card/exclusions-sidebar.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import { useIsMobile } from '@/composables/useIsMobile';
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import LazyMount from '@/components/next-card/lazy-mount.vue';

// Mobile sidebars state (must be declared before any watchers)
const isMobileFiltersOpen = ref(false);
const isMobileDisplayOpen = ref(false);
// Desktop switch user overlay
const showSwitchUserOverlay = ref(false);
const tempSelectedUserId = ref(null);
// Mobile display overlay
const showDisplayOverlay = ref(false);
// Bank search
const bankSearchQuery = ref('');

// Store instances
const appStore = useAppStore();
const nextCardStore = useNextCardStore();
const walletStore = useWalletStore();
const { isMobile } = useIsMobile();

// App Store methods and state
const userSettings = computed(() => appStore.userSettings);
const setShowAffiliateModal = appStore.setShowAffiliateModal;
const setShowNextCardMethodModal = appStore.setShowNextCardMethodModal;
const { hashedEmail } = storeToRefs(appStore);

// NextCard Store state
const loading = computed(() => nextCardStore.loading);
const loaded = computed(() => nextCardStore.loaded);
const users = computed(() => nextCardStore.users);
const activeUser = computed(() => nextCardStore.activeUser);
const topCard = computed(() => nextCardStore.topCard);
const topCardMatchesSearch = computed(() => nextCardStore.topCardMatchesSearch);
const nextCards = computed(() => nextCardStore.nextCards);
const pageNumber = computed(() => nextCardStore.pageNumber);
const pageSize = computed(() => nextCardStore.pageSize);
const omittedCards = computed(() => nextCardStore.omittedCards);
const showFiltersMenu = computed(() => nextCardStore.showFiltersMenu);
const filteredNextCards = computed(() => nextCardStore.filteredNextCards);
const paginatedNextCards = computed(() => nextCardStore.paginatedNextCards);
const pageCount = computed(() => nextCardStore.pageCount);
const showExclusionsSidebar = computed(
    () => nextCardStore.showExclusionsSidebar
);
const activeNextCard = computed(() => nextCardStore.activeNextCard);
const offenders = computed(() => nextCardStore.offenders);
const fiveTwentyFourCount = computed(() => nextCardStore.fiveTwentyFourCount);
const showFive24FilterCallout = computed(
    () => nextCardStore.showFive24FilterCallout
);
const showHidden = computed(() => nextCardStore.showHidden);
const show524 = computed(() => nextCardStore.show524);

// Filters and options for desktop bar (design-only)
const filters = computed(() => nextCardStore.filters);
const types = computed(() => nextCardStore.types);
const programTypes = computed(() => nextCardStore.programTypes);
const banks = computed(() => nextCardStore.banks);
const spends = computed(() => nextCardStore.spends);

const page = usePage();
const inertiaInstitutions = computed(() => {
    const p = page?.props?.value || page?.props || {};
    return p.institutions || [];
});

const institutions = computed(() => inertiaInstitutions.value);

// Memoized allBanks list: rebuild only when banks/institutions change
const allBanksMemo = ref([]);
const buildAllBanks = () => {
    const byCode = new Map();

    const normalize = (item) => {
        if (!item) return null;
        const code = (item.code || item.institution_code || '').toString();
        const name = item.name || '';
        const logo = item.logo || {
            small: item.small_logo_url || null,
            medium: item.medium_logo_url || null,
        };
        return {
            code,
            name,
            lowerName: (name || '').toString().toLowerCase(),
            enabled: item.enabled ?? true,
            checked: item.checked ?? false,
            logo,
            small_logo_url: item.small_logo_url ?? logo.small ?? null,
            medium_logo_url: item.medium_logo_url ?? logo.medium ?? null,
        };
    };

    (banks.value || []).forEach((b) => {
        if (!b) return;
        const norm = normalize(b);
        if (!norm || !norm.code) return;
        byCode.set(norm.code.toLowerCase(), norm);
    });

    (inertiaInstitutions.value || []).forEach((ins) => {
        const norm = normalize(ins);
        if (!norm || !norm.code) return;
        const key = norm.code.toLowerCase();
        const existing = byCode.get(key);
        if (existing) {
            byCode.set(key, {
                ...existing,
                logo: norm.logo || existing.logo,
                small_logo_url: norm.small_logo_url || existing.small_logo_url,
                medium_logo_url:
                    norm.medium_logo_url || existing.medium_logo_url,
            });
        }
    });

    allBanksMemo.value = Array.from(byCode.values()).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
    );
};

watch([banks, inertiaInstitutions], buildAllBanks, { immediate: true });

const filteredBanks = computed(() => {
    if (!bankSearchQuery.value || bankSearchQuery.value.trim() === '') {
        return allBanksMemo.value;
    }

    const query = bankSearchQuery.value.toLowerCase().trim();
    return allBanksMemo.value.filter((bank) => {
        const ln = bank.lowerName || (bank.name || '').toLowerCase();
        return ln.includes(query);
    });
});

const activeUserName = computed(() => {
    const au = activeUser.value;
    if (au && au.name) return au.name;
    const first =
        (users.value || []).find((u) => u && u.show_user) ||
        (users.value || [])[0];
    return first?.name || '';
});

const availableBonusCategories = computed(() => {
    // Generate categories dynamically from actual spend_bonus_categories in cards
    const categoriesMap = new Map();
    const cards = filteredNextCards.value || [];

    const categoryNormalizer = (name) => {
        if (!name) return null;

        const lowered = name.toLowerCase();

        if (lowered.includes('air travel')) return 'Travel';
        if (lowered.includes('prepaid hotels')) return 'Hotels';
        if (lowered.includes('ihg') && lowered.includes('hotel'))
            return 'Hotels';
        if (lowered.includes('united') && lowered.includes('airline'))
            return 'Airlines';
        if (
            lowered === 'all travel (ultimate rewards)' ||
            lowered === 'all travel'
        )
            return 'Travel';
        if (lowered.includes('old navy') || lowered.includes('gap'))
            return 'Shopping';
        if (lowered.includes('starbucks')) return 'Coffee Shops';
        if (
            lowered === 'ridesharing' ||
            lowered === 'lyft' ||
            lowered === 'uber'
        )
            return 'Ridesharing';
        if (lowered === 'gas stations' || lowered === 'gas')
            return 'Gas Stations';
        if (lowered === 'grocery stores' || lowered === 'groceries')
            return 'Grocery Stores';
        if (lowered === 'dining' || lowered === 'restaurants') return 'Dining';
        if (lowered === 'streaming services' || lowered === 'streaming')
            return 'Streaming';
        if (lowered === 'department stores') return 'Department Stores';
        if (lowered === 'transit' || lowered === 'public transit')
            return 'Transit';
        if (lowered === 'tolls') return 'Tolls';
        if (lowered === 'travel') return 'Travel';
        if (lowered === 'hotels') return 'Hotels';
        if (lowered === 'airlines' || lowered === 'airfare') return 'Airlines';

        return name;
    };

    const normalizeCategoryForFilter = (name) => {
        if (!name) return null;
        const lowered = name.toLowerCase();

        if (lowered.includes('air travel')) return 'travel';
        if (lowered.includes('prepaid hotels')) return 'hotels';
        if (lowered.includes('ihg') && lowered.includes('hotel'))
            return 'hotels';
        if (lowered.includes('united') && lowered.includes('airline'))
            return 'airlines';
        if (
            lowered === 'all travel (ultimate rewards)' ||
            lowered === 'all travel'
        )
            return 'travel';
        if (lowered.includes('old navy') || lowered.includes('gap'))
            return 'shopping';
        if (lowered.includes('starbucks')) return 'coffee_shops';
        if (
            lowered === 'ridesharing' ||
            lowered === 'lyft' ||
            lowered === 'uber'
        )
            return 'ridesharing';
        if (lowered === 'gas stations' || lowered === 'gas')
            return 'gas_stations';
        if (lowered === 'grocery stores' || lowered === 'groceries')
            return 'grocery_stores';
        if (lowered === 'dining' || lowered === 'restaurants') return 'dining';
        if (lowered === 'streaming services' || lowered === 'streaming')
            return 'streaming';
        if (lowered === 'department stores') return 'department_stores';
        if (lowered === 'transit' || lowered === 'public transit')
            return 'transit';
        if (lowered === 'tolls') return 'tolls';
        if (lowered === 'travel') return 'travel';
        if (lowered === 'hotels') return 'hotels';
        if (lowered === 'airlines' || lowered === 'airfare') return 'airlines';

        return name
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, '');
    };

    const categoryCards = new Map();

    cards.forEach((card) => {
        const rewardsData = card.rewardscc_data || card.rewardsccData;

        if (rewardsData && rewardsData.spend_bonus_categories) {
            const spendCategories = rewardsData.spend_bonus_categories;

            if (Array.isArray(spendCategories) && spendCategories.length > 0) {
                spendCategories.forEach((cat) => {
                    const originalName = cat.spendBonusCategoryName;
                    const group = cat.spendBonusCategoryGroup;

                    if (originalName) {
                        const normalizedName = categoryNormalizer(originalName);

                        if (normalizedName) {
                            const key =
                                normalizeCategoryForFilter(originalName);

                            if (!categoryCards.has(key)) {
                                categoryCards.set(key, new Set());
                            }
                            categoryCards.get(key).add(card.id);

                            if (!categoriesMap.has(key)) {
                                categoriesMap.set(key, {
                                    value: key,
                                    label: normalizedName,
                                    group: group || 'Other',
                                });
                            }
                        }
                    }
                });
            }
        }
    });

    if (categoriesMap.size > 0) {
        const categories = Array.from(categoriesMap.entries())
            .map(([key, data]) => {
                const cardSet = categoryCards.get(key) || new Set();
                return {
                    ...data,
                    count: cardSet.size,
                };
            })
            .filter((cat) => cat.count > 0)
            .sort((a, b) => {
                if (b.count !== a.count) {
                    return b.count - a.count;
                }
                if (a.group !== b.group) {
                    return a.group.localeCompare(b.group);
                }
                return a.label.localeCompare(b.label);
            });

        return categories;
    }

    return [];
});

// Switch User overlay handlers (desktop)
const openSwitchUserOverlay = () => {
    const au = activeUser.value || {};
    tempSelectedUserId.value = au.id ?? au.uuid ?? au.name ?? null;
    showSwitchUserOverlay.value = true;
};
const closeSwitchUserOverlay = () => {
    showSwitchUserOverlay.value = false;
};
const applySwitchUserOverlay = () => {
    const list = users.value || [];
    const findMatch = (u) => {
        const key = tempSelectedUserId.value;
        return u?.id === key || u?.uuid === key || u?.name === key;
    };
    const selected = list.find(findMatch);
    if (selected) {
        loadNextCards(selected);
    }
    showSwitchUserOverlay.value = false;
};

const selectedTypesCount = computed(() => filters.value?.types?.length || 0);
const selectedProgramsCount = computed(
    () => filters.value?.programs?.length || 0
);
const selectedBanksCount = computed(() => filters.value?.banks?.length || 0);
const selectedSpendsCount = computed(() => filters.value?.spends?.length || 0);
const selectedFeesCount = computed(() => filters.value?.fees?.length || 0);
const selectedBonusCategoriesCount = computed(
    () => filters.value?.bonusCategories?.length || 0
);
const selectedWelcomeBonusCount = computed(
    () => filters.value?.welcomeBonus?.length || 0
);
const hasAnySelections = computed(
    () =>
        selectedTypesCount.value +
            selectedProgramsCount.value +
            selectedBanksCount.value +
            selectedSpendsCount.value +
            selectedFeesCount.value +
            selectedBonusCategoriesCount.value +
            selectedWelcomeBonusCount.value >
        0
);
const totalSelections = computed(
    () =>
        selectedTypesCount.value +
        selectedProgramsCount.value +
        selectedBanksCount.value +
        selectedSpendsCount.value +
        selectedFeesCount.value +
        selectedBonusCategoriesCount.value +
        selectedWelcomeBonusCount.value
);

const isOver524 = computed(() => fiveTwentyFourCount.value >= 5);
const isChaseFilterActive = computed(() => {
    const bankFilters = filters.value?.banks || [];
    return bankFilters.some((bankCode) => {
        const bank = banks.value?.find((b) => b.code === bankCode);
        return (
            bank &&
            (bank.name?.toLowerCase().includes('chase') ||
                bank.code?.toLowerCase().includes('chase'))
        );
    });
});
const showOver524ChaseState = computed(() => {
    return (
        isOver524.value &&
        isChaseFilterActive.value &&
        visibleFilteredNextCards.value.length === 0 &&
        loaded.value &&
        !isSearching.value
    );
});

// Annual fee options (fixed list)
const annualFeeOptions = computed(() => [
    { value: 0, label: 'None' },
    { value: 95, label: '$95 and below' },
    { value: 250, label: '$250 and below' },
    { value: 395, label: '$395 and below' },
    { value: '500+', label: '$500+' },
]);

// NextCard Store methods
const getNextCards = nextCardStore.getNextCards;
const loadNextCards = nextCardStore.loadNextCards;
const getActiveUser = nextCardStore.getActiveUser;
const goodProgramName = nextCardStore.goodProgramName;
const toggleFilter = nextCardStore.toggleFilter;
const toggleFiltersMenu = nextCardStore.toggleFiltersMenu;
const setExclusionsSidebar = nextCardStore.setExclusionsSidebar;
const setPageSize = nextCardStore.setPageSize;
const clearFilters = nextCardStore.clearFilters;
const setShowHidden = nextCardStore.setShowHidden;
const setShow524 = nextCardStore.setShow524;
const setFive24shownCookie = nextCardStore.setFive24shownCookie;
const setSearchTerm = nextCardStore.setSearchTerm;
const gotoPage = nextCardStore.gotoPage;
const nextPage = nextCardStore.nextPage;
const prevPage = nextCardStore.prevPage;

// Computed properties
const localPageSize = computed({
    get: () => pageSize.value,
    set: (val) => setPageSize(val),
});

// Methods

// Reset all filters + search
const resetAll = () => {
    clearFilters();
    search.value = '';
    gotoPage(0);
};

const scrollTopPosStyle = computed(() => {
    const base = 100;
    return {
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${base}px)`,
    };
});

const hashEmail = computed(() => {
    return hashedEmail.value;
});

// Minimal search state (UI only)
const search = ref('');
const clearSearch = () => {
    search.value = '';
    debouncedSearch.value = '';
    isSearching.value = false;
    // keep store in sync
    setSearchTerm('');
};
const debouncedSearch = ref('');
const isSearching = ref(false);
const MIN_SPINNER_MS = 1000;
let searchDebounceHandle = null;
let spinnerHideHandle = null;
let searchStartAt = 0;
watch(search, (newVal) => {
    // clear any pending timers
    if (searchDebounceHandle) clearTimeout(searchDebounceHandle);
    if (spinnerHideHandle) clearTimeout(spinnerHideHandle);

    const term = (newVal || '').toString();
    const hasTerm = term.trim() !== '';
    if (!hasTerm) {
        debouncedSearch.value = '';
        isSearching.value = false;
        setSearchTerm('');
        return;
    }

    // start spinner immediately
    isSearching.value = true;
    searchStartAt = Date.now();

    // debounce applying the term
    searchDebounceHandle = setTimeout(() => {
        debouncedSearch.value = term;
        setSearchTerm(term);
        const elapsed = Date.now() - searchStartAt;
        const remaining = MIN_SPINNER_MS - elapsed;
        if (remaining > 0) {
            spinnerHideHandle = setTimeout(() => {
                isSearching.value = false;
            }, remaining);
        } else {
            isSearching.value = false;
        }
    }, 450);
});
const visibleFilteredNextCards = computed(() => {
    const list = nextCardStore.filteredNextCardsWithSearch;
    return Array.isArray(list) ? list : [];
});

const visiblePaginatedNextCards = computed(() => {
    const start = pageNumber.value * pageSize.value,
        end = start + pageSize.value;
    return visibleFilteredNextCards.value.slice(start, end);
});

const uiPageCount = computed(() => {
    const l = visibleFilteredNextCards.value.length,
        s = pageSize.value || 1;
    return Math.ceil(l / s);
});

const searchedCount = computed(
    () => visibleFilteredNextCards.value.length + (topCardMatchesSearch.value ? 1 : 0)
);

const useLazyMount = computed(() => {
    const env = (import.meta?.env?.VITE_NC_LAZY_MOUNT || '').toString().toLowerCase();
    if (env === 'true') return true;
    if (env === 'false') return false;
    return isMobile.value;
});
const lazyRootMargin = computed(() => (isMobile.value ? '0px 0px 300px 0px' : '0px 0px 200px 0px'));

// Pagination handler (maps 1-based Pagination to 0-based store)
const handlePageChange = (page) => {
    const total = uiPageCount.value;
    let p = Number(page) || 1;
    if (p < 1) p = 1;
    if (p > total) p = total;
    gotoPage(p - 1);
};

// Scroll to top functionality
const showScrollTop = ref(false);
const scrollEl = ref(null);

const getScrollCandidates = () => {
    return [
        document.querySelector('#f-body'),
        document.getElementById('inner-app'),
        document.scrollingElement || document.documentElement || document.body,
        document.documentElement,
        document.body,
    ].filter(Boolean);
};

const pickScrollEl = () => {
    const candidates = getScrollCandidates();
    for (const c of candidates) {
        const canScroll = (c.scrollHeight || 0) - (c.clientHeight || 0) > 1;
        if ((c.scrollTop || 0) > 0 || canScroll) {
            scrollEl.value = c;
            return;
        }
    }
    scrollEl.value = candidates[0] || null;
};

const handleScroll = () => {
    const element = scrollEl.value || document.querySelector('#f-body');
    if (!element) return;
    showScrollTop.value = (element.scrollTop || 0) > 400;
};

const smoothScrollTo = (el, to = 0, duration) => {
    const start = el.scrollTop;
    const change = to - start;
    const distance = Math.abs(change);
    const dur =
        typeof duration === 'number'
            ? duration
            : Math.max(350, Math.min(1200, distance / 2));
    const startTime = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / dur, 1);
        el.scrollTop = start + change * easeOutCubic(progress);
        if (elapsed < dur) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
};

const scrollToTop = () => {
    const targets = [];
    const el = document.querySelector('#f-body');
    if (el) targets.push(el);
    const se =
        document.scrollingElement || document.documentElement || document.body;
    if (se && se !== el) targets.push(se);
    const docEl = document.documentElement;
    if (docEl && docEl !== se && docEl !== el) targets.push(docEl);
    const body = document.body;
    if (body && body !== se && body !== docEl && body !== el)
        targets.push(body);
    let animated = false;
    for (const t of targets) {
        if (t && (t.scrollTop > 0 || t.scrollHeight > t.clientHeight)) {
            smoothScrollTo(t, 0);
            animated = true;
        }
    }
    if (!animated && el) smoothScrollTo(el, 0);
};

// Watchers
watch(pageNumber, () => {
    let element = document.querySelector('#f-body');
    element.scrollTop = 0;
});

watch(debouncedSearch, () => {
    gotoPage(0);
});

watch(isMobile, (val) => {
    setPageSize(val ? 8 : 12);
});

const anyOverlayOpen = computed(() => {
    return (
        !!(typeof showFiltersMenu !== 'undefined' && showFiltersMenu?.value) ||
        !!(typeof isMobileFiltersOpen !== 'undefined' && isMobileFiltersOpen?.value) ||
        !!(typeof isMobileDisplayOpen !== 'undefined' && isMobileDisplayOpen?.value) ||
        !!(typeof showSwitchUserOverlay !== 'undefined' && showSwitchUserOverlay?.value) ||
        !!(typeof showDisplayOverlay !== 'undefined' && showDisplayOverlay?.value)
    );
});

watch(anyOverlayOpen, (open) => {
    if (typeof document !== 'undefined') {
        if (open) document.body.classList.add('overflow-hidden');
        else document.body.classList.remove('overflow-hidden');
    }
});

onUnmounted(() => {
    if (typeof document !== 'undefined') {
        document.body.classList.remove('overflow-hidden');
    }
});

// Openers
const openMobileFiltersModal = () => {
    isMobileFiltersOpen.value = true;
};
const openDisplayOverlay = () => {
    showDisplayOverlay.value = true;
};
const closeDisplayOverlay = () => {
    showDisplayOverlay.value = false;
};

// Mobile filters actions
const handleClearAllMobile = () => {
    clearFilters();
    gotoPage(0);
};
const applyMobileFilters = () => {
    isMobileFiltersOpen.value = false;
    gotoPage(0);
};

// Lifecycle hooks
onMounted(async () => {
    setPageSize(isMobile.value ? 8 : 12);
    await getNextCards();

    pickScrollEl();
    if (scrollEl.value) {
        scrollEl.value.addEventListener('scroll', handleScroll, {
            passive: true,
        });
        handleScroll();
    }
    window.addEventListener('scroll', handleScroll);

    if (
        import.meta.env.VITE_APP_ENV === 'production' &&
        typeof fbq !== 'undefined'
    ) {
        const dashboardStore = useDashboardStore();
        if (
            dashboardStore.user &&
            dashboardStore.user.hashed_email_sha256 &&
            dashboardStore.user.uuid
        ) {
            const event_id = generateUUID();

            // Facebook Pixel Event
            fbq(
                'track',
                'ViewContent',
                {
                    content_name: 'MyNextCardPage',
                    content_category: 'CardRecommendation',
                    content_type: 'product',
                    em: dashboardStore.user.hashed_email_sha256,
                    external_id: dashboardStore.user.uuid,
                },
                { eventID: event_id }
            );
            // Woopra Event
            if (typeof woopra !== 'undefined') {
                woopra.track('visit-mynextcardpage', {
                    email: dashboardStore.user.email, // Woopra typically uses raw email
                    uuid: dashboardStore.user.uuid,
                    fb_event_id: event_id,
                });
            }
        }
    }
});

const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return /^https?:\/\/.+/.test(url);
};

const isDefaultLogo = (url) => {
    if (!url) return true;
    return (
        url.includes('default_50x50.png') || url.includes('default_100x100.png')
    );
};

const bankLogo = (option) => {
    if (!option) return null;

    const logoSources = [
        option.logo?.small,
        option.logo?.medium,
        option.small_logo_url,
        option.medium_logo_url,
    ];

    for (const logoUrl of logoSources) {
        if (isValidUrl(logoUrl) && !isDefaultLogo(logoUrl)) {
            return logoUrl;
        }
    }

    return null;
};

const getBankInitials = (option) => {
    if (!option) return '?';
    const name = option.name || '';
    const words = name.split(' ').filter((word) => word.length > 0);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }
    return '?';
};

const failedLogos = ref(new Set());

const onBankLogoError = (e, option) => {
    const el = e.target;
    failedLogos.value.add(option.code);
    el.style.display = 'none';
};

const shouldShowLogo = (option) => {
    return bankLogo(option) && !failedLogos.value.has(option.code);
};

onBeforeUnmount(() => {
    if (scrollEl.value) {
        scrollEl.value.removeEventListener('scroll', handleScroll);
    }
});
</script>

<style lang="scss" scoped>
.exclusions-sidebar-open {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(-448px);
}
.exclusions-sidebar-closed {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(0);
}
label {
    &.disabled {
        opacity: 0.45;
    }
}
/* Toggle switch styles (matching ProgramPage design) */
.slider {
    border-radius: 25px;
    transition: background-color 0.3s ease-in-out;
    &:before {
        position: absolute;
        content: '';
        height: 19px;
        width: 19px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.3s ease-in-out;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
    &:hover {
        background-color: #9ca3af; /* gray-400 for hover state */
    }
}
input:checked + .slider {
    background-color: #fd421d !important;
    &:hover {
        background-color: #dc2626 !important; /* darker red for hover when checked */
    }
}
input:active + .slider {
    background-color: #6b7280; /* gray-500 for pressed state */
}
input:checked:active + .slider {
    background-color: #b91c1c !important; /* even darker red for pressed when checked */
}
input:checked + .slider:before {
    transform: translateX(17px);
}
/* Custom pagination styles to mirror Transfer Partners Guide */
.custom-pagination :deep(nav) {
    gap: 8px;
}

.custom-pagination :deep(button[aria-current='page']) {
    min-width: 36px;
    padding: 8px;
    border-radius: 4px;
}

/* Active page button with dark background */
.custom-pagination :deep(button[aria-current='page'].text-orange-500) {
    background-color: #1e293b !important; /* slate-800 */
    color: #ffffff !important;
    font-weight: 500;
}

/* Inactive page buttons */
.custom-pagination :deep(button[aria-current='page']:not(.text-orange-500)) {
    background-color: transparent;
    color: #1e293b;
}

.custom-pagination
    :deep(button[aria-current='page']:not(.text-orange-500):hover) {
    background-color: #f1f5f9; /* slate-100 */
}

/* Dark mode: inactive page numbers were forced to #1e293b (invisible on
   the dark page). .custom-pagination carries this component's scope id. */
.dark .custom-pagination :deep(button[aria-current='page'].text-orange-500) {
    background-color: #334155 !important; /* slate-700 */
}

.dark
    .custom-pagination
    :deep(button[aria-current='page']:not(.text-orange-500)) {
    color: #cbd5e1; /* slate-300 */
}

.dark
    .custom-pagination
    :deep(button[aria-current='page']:not(.text-orange-500):hover) {
    background-color: #334155; /* slate-700 */
    color: #f1f5f9;
}
</style>
